import { create } from 'zustand';
import type {
  EmojiReaction,
  MatchResult,
  MatchView,
  PairStats,
  PublicUser,
  Seat,
  ServerMessage,
} from '@sf72/shared';
import { GameSocket, type ConnectionStatus } from '../net/socket.js';
import { haptic, setHapticsEnabled } from '../telegram.js';
import { play, playEngineSound, setSoundEnabled } from '../sound.js';

/**
 * The single source of truth on the client.
 *
 * It holds only what the server sent. Nothing here computes a game outcome or
 * decides whether a move is legal — the client draws what it is told and asks
 * for what the player wants, and the server answers.
 */

export interface FloatingEmoji {
  id: number;
  fromSeat: Seat;
  emoji: EmojiReaction;
}

export interface PendingOffer {
  kind: 'draw' | 'undo' | 'rematch';
  fromSeat: Seat;
  expiresAt: number;
}

interface MatchStore {
  status: ConnectionStatus;
  user: PublicUser | null;
  error: string | null;

  match: MatchView | null;
  /** Engine state, already masked for this player by the server. */
  state: unknown;
  yourSeat: Seat | null;
  legalMoves: unknown[] | null;

  offer: PendingOffer | null;
  result: MatchResult | null;
  pair: { game: PairStats; overall: PairStats } | null;
  /** Uzbek lines the engine emitted, newest last. */
  log: string[];
  emojis: FloatingEmoji[];
  /** Set while a move is in flight, so the board can disable itself. */
  submitting: boolean;
  /** Server clock minus local clock, used to render an honest countdown. */
  clockSkewMs: number;

  connect: () => void;
  disconnect: () => void;
  join: (matchId: string) => void;
  sendMove: (move: unknown) => void;
  sendEmoji: (emoji: EmojiReaction) => void;
  resign: () => void;
  offerDraw: () => void;
  answerDraw: (accept: boolean) => void;
  requestUndo: () => void;
  answerUndo: (accept: boolean) => void;
  offerRematch: () => void;
  answerRematch: (accept: boolean) => void;
  dismissError: () => void;
}

let socket: GameSocket | null = null;
let emojiId = 0;

/**
 * How long to wait for the server to acknowledge a move.
 *
 * Generous: a slow phone on a slow network is not the same as a lost move,
 * and clearing too early would let a player submit twice.
 */
const MOVE_TIMEOUT_MS = 12_000;

/** Identifies the move currently in flight, so a stale watchdog does nothing. */
let moveToken = 0;

export const useMatch = create<MatchStore>((set, get) => {
  const handle = (message: ServerMessage): void => {
    switch (message.t) {
      case 'auth_ok':
        // Toggled in the bot, honoured here. Without this the settings screen
        // had no effect on the half of the app that actually makes noise.
        setSoundEnabled(message.settings.sound);
        setHapticsEnabled(message.settings.vibration);
        set({ user: message.user, error: null });
        return;

      case 'error':
        // A stale sequence just means the board moved on; re-syncing is
        // automatic and telling the player about it would be noise.
        if (message.code === 'stale_seq') {
          set({ submitting: false });
          return;
        }
        play('error');
        haptic('error');
        set({ error: message.message, submitting: false });
        return;

      case 'state':
        set({
          match: message.match,
          state: message.state,
          yourSeat: message.yourSeat,
          legalMoves: message.legalMoves ?? null,
          result: message.match.result ?? null,
          submitting: false,
        });
        return;

      case 'move': {
        const match = get().match;
        // Drop anything that is not strictly newer than what is on screen.
        if (match && message.seq <= match.seq) return;

        set({
          state: message.state,
          legalMoves: message.legalMoves ?? null,
          submitting: false,
          match: match
            ? {
                ...match,
                seq: message.seq,
                turnSeat: message.turnSeat,
                turnDeadline: message.turnDeadline,
              }
            : match,
        });

        if (message.bySeat !== get().yourSeat) {
          play('place');
          haptic('light');
        }
        return;
      }

      case 'presence': {
        const match = get().match;
        if (!match) return;
        set({
          match: {
            ...match,
            players: match.players.map((player) =>
              player.seat === message.seat ? { ...player, online: message.online } : player,
            ),
          },
        });
        return;
      }

      case 'emoji': {
        const item: FloatingEmoji = {
          id: ++emojiId,
          fromSeat: message.fromSeat,
          emoji: message.emoji,
        };
        set({ emojis: [...get().emojis, item] });
        window.setTimeout(() => {
          set({ emojis: get().emojis.filter((e) => e.id !== item.id) });
        }, 2500);
        return;
      }

      case 'offer':
        set({
          offer: {
            kind: message.kind,
            fromSeat: message.fromSeat,
            expiresAt: message.expiresAt,
          },
        });
        if (message.fromSeat !== get().yourSeat) {
          play('notify');
          haptic('warning');
        }
        return;

      case 'offer_resolved':
        set({ offer: null });
        if (message.kind === 'rematch' && message.accepted && message.newMatchId) {
          get().join(message.newMatchId);
        }
        return;

      case 'end': {
        const yourSeat = get().yourSeat;
        const won = message.result.winnerSeat === yourSeat;
        const drew = message.result.winnerSeat === null;

        play(drew ? 'draw' : won ? 'win' : 'lose');
        haptic(drew ? 'warning' : won ? 'success' : 'error');

        const match = get().match;
        set({
          result: message.result,
          pair: message.pair,
          state: message.state,
          offer: null,
          submitting: false,
          match: match
            ? { ...match, status: 'finished', result: message.result, turnDeadline: null }
            : match,
        });
        return;
      }

      case 'pong':
        set({ clockSkewMs: message.now - Date.now() });
        return;
    }
  };

  const require = (): { socket: GameSocket; matchId: string } | null => {
    const matchId = get().match?.id;
    if (!socket || !matchId) return null;
    return { socket, matchId };
  };

  return {
    status: 'connecting',
    user: null,
    error: null,
    match: null,
    state: null,
    yourSeat: null,
    legalMoves: null,
    offer: null,
    result: null,
    pair: null,
    log: [],
    emojis: [],
    submitting: false,
    clockSkewMs: 0,

    connect: () => {
      if (socket) return;
      socket = new GameSocket({
        onMessage: handle,
        onStatus: (status) => set({ status }),
      });
      socket.connect();
    },

    disconnect: () => {
      socket?.disconnect();
      socket = null;
    },

    join: (matchId) => {
      // Everything below belongs to the match being left. A rematch joins
      // straight from the result card, so anything not cleared here is
      // carried into the new game: a seat that is no longer yours, a move
      // list for a position that no longer exists, or a move still counted
      // as in flight, which leaves the new board refusing every tap.
      moveToken++;
      set({
        match: null,
        state: null,
        yourSeat: null,
        legalMoves: null,
        result: null,
        pair: null,
        offer: null,
        submitting: false,
        log: [],
        error: null,
      });
      socket?.send({ t: 'join', matchId });
    },

    sendMove: (move) => {
      const context = require();
      if (!context) return;
      const match = get().match;
      if (!match || match.status !== 'active') return;
      if (get().submitting) return;

      // Offline, the socket would queue this and deliver it whenever the
      // connection returns — by which point the position has moved on and the
      // server discards it as stale, silently. The player would be left
      // believing they had moved. Say so instead.
      if (get().status !== 'ready') {
        play('error');
        haptic('error');
        set({ error: 'Aloqa yo‘q — ulanish tiklanishini kuting.' });
        return;
      }

      set({ submitting: true });
      play('select');
      haptic('selection');
      context.socket.send({
        t: 'move',
        matchId: context.matchId,
        move,
        expectSeq: match.seq,
      });

      // The board disables itself while a move is in flight, and only a reply
      // from the server turns it back on. If that reply never arrives the
      // board stays dead with nothing to explain why, which reads as a broken
      // game rather than a lost message.
      const token = ++moveToken;
      window.setTimeout(() => {
        if (token !== moveToken || !get().submitting) return;
        set({ submitting: false, error: 'Yurish yetib bormadi — qayta urinib ko‘ring.' });
      }, MOVE_TIMEOUT_MS);
    },

    sendEmoji: (emoji) => {
      const context = require();
      context?.socket.send({ t: 'emoji', matchId: context.matchId, emoji });
    },

    resign: () => {
      const context = require();
      context?.socket.send({ t: 'resign', matchId: context.matchId });
    },

    offerDraw: () => {
      const context = require();
      context?.socket.send({ t: 'draw', matchId: context.matchId });
    },

    answerDraw: (accept) => {
      const context = require();
      context?.socket.send({ t: 'draw', matchId: context.matchId, accept });
    },

    requestUndo: () => {
      const context = require();
      context?.socket.send({ t: 'undo', matchId: context.matchId });
    },

    answerUndo: (accept) => {
      const context = require();
      context?.socket.send({ t: 'undo', matchId: context.matchId, accept });
    },

    offerRematch: () => {
      const context = require();
      context?.socket.send({ t: 'rematch', matchId: context.matchId });
    },

    answerRematch: (accept) => {
      const context = require();
      context?.socket.send({ t: 'rematch', matchId: context.matchId, accept });
    },

    dismissError: () => set({ error: null }),
  };
});

/** Applies the sound and haptic cues an engine reported alongside a move. */
export function applyEngineEffects(effects: { type: string; name?: string }[]): void {
  for (const effect of effects) {
    if (effect.type === 'sound' && effect.name) playEngineSound(effect.name);
  }
}
